import { memo, useCallback, useMemo } from 'react';
import { useDeleteTiktokChannel } from '../../api/delete-tiktok-channel.api';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_TIKTOK_CHANNELS_QUERY_KEY } from '../../api/get-tiktok-channels.api';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteTiktokChannelModalType = 'DeleteTiktokChannelModal';
export interface IDeleteTiktokChannelModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_TIKTOK_CHANNEL = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete TikTok channel!',
};

const DeleteTiktokChannelModal = memo(({ organizationChannelId }: IDeleteTiktokChannelModalProps) => {
  const channelModalRootActions = useChannelModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteTiktokChannelModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteTiktokChannelModal');
  }, [channelModalRootActions]);

  const mutationDeleteTiktokChannel = useDeleteTiktokChannel({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_TIKTOK_CHANNEL.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_TIKTOK_CHANNELS_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteTiktokChannelModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_TIKTOK_CHANNEL.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteTiktokChannel.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }
  }, [organizationChannelId, mutationDeleteTiktokChannel]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteTiktokChannelModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteTiktokChannelModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteTiktokChannel.isPending}
      title="Delete TikTok Channel"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteTiktokChannelModal.displayName = 'DeleteTiktokChannelModal';

export default DeleteTiktokChannelModal;
